/** §27 — realtime event names and payloads, shared by server and every client. */
export const VENUE_EVENTS = {
  SESSION_OPENED: 'session.opened',
  SESSION_CLOSED: 'session.closed',
  ORDER_CREATED: 'order.created',
  ORDER_CONFIRMED: 'order.confirmed',
  ORDER_SENT_TO_KITCHEN: 'order.sent_to_kitchen',
  ITEM_PREPARING: 'item.preparing',
  ITEM_READY: 'item.ready',
  ORDER_READY: 'order.ready',
  ITEM_PICKED_UP: 'item.picked_up',
  ITEM_SERVED: 'item.served',
  ORDER_COMPLETED: 'order.completed',
  WAITER_REQUESTED: 'service_request.waiter',
  BILL_REQUESTED: 'service_request.bill',
  SERVICE_REQUEST_UPDATED: 'service_request.updated',
  PAYMENT_COMPLETED: 'payment.completed',
  CHECK_UPDATED: 'check.updated',
  TABLE_STATUS_CHANGED: 'table.status_changed',
  RESOURCE_SESSION_STARTED: 'resource_session.started',
  RESOURCE_SESSION_PAUSED: 'resource_session.paused',
  RESOURCE_SESSION_EXTENDED: 'resource_session.extended',
  RESOURCE_SESSION_ENDED: 'resource_session.ended',
  RESOURCE_SESSION_EXPIRING: 'resource_session.expiring',
  TAKEAWAY_RELEASED: 'takeaway.released',
  TAKEAWAY_READY: 'takeaway.ready',
  TAKEAWAY_HANDED_OVER: 'takeaway.handed_over',
  /**
   * The manager approval queue. It used to ride `spending_approval.*`, shared
   * with the guest host queue that answered for a party's spending; that queue
   * went with Party and the gate now has names of its own. No guest client
   * subscribes to these — a guest token may not join a branch or user room.
   */
  POLICY_APPROVAL_REQUESTED: 'policy_approval.requested',
  POLICY_APPROVAL_RESOLVED: 'policy_approval.resolved',
} as const;

export type VenueEventName = (typeof VENUE_EVENTS)[keyof typeof VENUE_EVENTS];

export interface BaseEventPayload {
  tenantId: string;
  branchId: string;
  occurredAt: string;
  actorId?: string;
}

export interface OrderEventPayload extends BaseEventPayload {
  orderId: string;
  orderNumber: string;
  status: string;
  fulfillmentType: string;
  sessionId?: string | null;
  tableId?: string | null;
}

export interface OrderItemEventPayload extends BaseEventPayload {
  orderId: string;
  orderItemId: string;
  itemName: string;
  status: string;
  stationId?: string | null;
  stationName?: string | null;
}

export interface TableEventPayload extends BaseEventPayload {
  tableId: string;
  areaId: string;
  floorId: string;
  state: string;
}

export interface ResourceSessionEventPayload extends BaseEventPayload {
  resourceSessionId: string;
  resourceId: string;
  status: string;
  remainingSeconds?: number;
  tableId?: string | null;
}

export interface ServiceRequestEventPayload extends BaseEventPayload {
  serviceRequestId: string;
  type: string;
  status: string;
  areaId?: string | null;
  tableId?: string | null;
  assigneeId?: string | null;
}

/** Socket.IO room names — the only authorization boundary on the wire. */
export const rooms = {
  tenant: (id: string) => `tenant:${id}`,
  branch: (id: string) => `branch:${id}`,
  floor: (id: string) => `floor:${id}`,
  area: (id: string) => `area:${id}`,
  table: (id: string) => `table:${id}`,
  station: (id: string) => `station:${id}`,
  session: (id: string) => `session:${id}`,
  order: (id: string) => `order:${id}`,
  resource: (id: string) => `resource:${id}`,
  user: (id: string) => `user:${id}`,
  guest: (sessionId: string) => `guest:${sessionId}`,
};
